import { useState, useMemo } from 'react';
import styles from "./dashboard.module.scss";


type BarChartItemProps = {
  label: string;
  value: number;
  percent: number;
  color: 'mailed' | 'shipped';
};

function BarChartItem({ label, value, percent, color }: BarChartItemProps) {

  const barClass = color === 'mailed' ? styles.mailedBar : styles.shippedBar;
  
  return (
    <div className={styles.chartItem}>
      <div className={styles.label}>{label}</div>
      <div className={styles.barWrapper}>
        <div 
          className={`${styles.bar} ${barClass}`}
          style={{ width: `${percent}%` }}
        >
          <p className={styles.barValue}>{value.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}

// Helper function to convert camelCase to Title Case
function formatLabel(str: string): string {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase());
}

const DeliveryMethodSummary: React.FC<any> = ({dashboardData}) => {

  const [mode, setMode] = useState<'pieces' | 'orders'>('pieces');
  const data = dashboardData?.deliveryMethodSummary;  

  // Process the data to generate chart items
  const items = useMemo(() => {
    if (!data) return [];

    const mapped = [
      ...Object.entries(data.mailed || {}).map(([key, val]:[string, any]) => ({
        label: formatLabel(key),
        type: 'mailed' as const,
        value: val[mode],
      })),
      ...Object.entries(data.shipped || {}).map(([key, val]:[string, any]) => ({
        label: formatLabel(key),
        type: 'shipped' as const,
        value: val[mode],
      })),
    ];

    // ✅ remove zero values
    const filtered = mapped.filter(item => item.value > 0);
    const max = Math.max(...mapped.map((i) => i.value), 1);

    return filtered.map((item) => ({
      ...item,
      percent: Math.round((item.value / max) * 100), // rounded %
    }));
  }, [data, mode]);

  const hasServiceLevels = items.length > 0;  

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.title}>
            <p>Delivery Method</p>
          </div>
          <div className={styles.textSwitch}>
            <div 
              className={mode === 'pieces' ? styles.switchButtonActive : styles.switchButtonInactive}
              onClick={() => setMode('pieces')}
            >
              {mode === 'pieces' && <div className={styles.switchBorder} />}
              <div className={mode === 'pieces' ? styles.switchText : styles.switchTextInactive}>
                <p>Pieces</p>
              </div>
            </div>
            <div 
              className={mode === 'orders' ? styles.switchButtonActive : styles.switchButtonInactive}
              onClick={() => setMode('orders')}
            >
              {mode === 'orders' && <div className={styles.switchBorder} />}
              <div className={mode === 'orders' ? styles.switchText : styles.switchTextInactive}>
                <p>Orders</p>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.legend}>
          <div className={styles.legendItem}>
            <div className={styles.legendDot}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <circle cx="5" cy="5" r="5" fill="#249AF3" />
              </svg>
            </div>
            <div className={styles.legendText}>
              <p>Mailed</p>
            </div>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendDot}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <circle cx="5" cy="5" r="5" fill="#0A4AA1" />
              </svg>
            </div>
            <div className={styles.legendText}> 
              <p>Shipped</p>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.chartList}>
        {!hasServiceLevels ? (
          <p>No Service Levels available for {mode}</p>
          ) : ( items.map((item, index) => (
              <BarChartItem
                key={`${item.label}-${index}`}
                label={item.label}
                value={item.value}
                percent={item.percent}
                color={item.type}
              />))
            )
        }
      </div>
    </div>
  );
}

export default DeliveryMethodSummary;